<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)">
    <template #content>
      <ZipFileGenAnimMiniIllustration style="width: 130px; margin: 0 auto 10px" />

      <template v-if="!isReady">
        <h1 class="global-h1" style="margin-bottom: 15px; text-align: center">We are compiling a ZIP archive of files for you</h1>
        <p class="global-p" style="text-align: center">
          <b>Wait here for a minute...<br /></b>
          Or we'll notify you via email when it is ready.
        </p>
      </template>

      <template v-else>
        <h1 class="global-h1" style="margin-bottom: 15px; text-align: center">It's ready!</h1>
        <div style="display: flex; justify-content: center">
          <SrpButton @click="handleDownload">Download</SrpButton>
        </div>
      </template>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import AssetsDownloadRequestRepo from "@repos/assetsDownloadRequestRepo";
import FileDownload from "@logic/FileDownload";

// Components
import ZipFileGenAnimMiniIllustration from "@components/AnimatedMiniIllustrations/ZipFileGenAnimMiniIllustration.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    contextId: string;
    collabInputId?: string | null;
    uploadCampaignId?: string | null;
    uploaderEmail?: string | null;
    locationExternalId?: string | null;
  }>(),
  {
    isVisible: false,
    collabInputId: null,
    uploadCampaignId: null,
    uploaderEmail: null,
    locationExternalId: null,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
}>();

const isReady = ref<boolean>(false);
const downloadFileLocation = ref<string | null>(null);
let pollInterval: ReturnType<typeof setInterval> | null = null;

async function checkDownloadStatus() {
  if (!props.customerId || !props.contextId) return;

  const downloadRequest = await AssetsDownloadRequestRepo.LoadByContextId(
    props.customerId,
    props.contextId,
    false,
    props.collabInputId,
    props.uploadCampaignId,
    props.uploaderEmail,
    props.locationExternalId
  );

  if (downloadRequest.completedDate && downloadRequest.downloadFileLocation) {
    isReady.value = true;
    downloadFileLocation.value = downloadRequest.downloadFileLocation;
    stopPolling();
  }
}

function startPolling() {
  // eslint-disable-next-line no-console
  console.log("DownloadModal: Starting polling");
  // Check immediately
  checkDownloadStatus();

  // Then poll every 15 seconds
  pollInterval = setInterval(() => {
    checkDownloadStatus();
  }, 15000);
}

function stopPolling() {
  // eslint-disable-next-line no-console
  console.log("DownloadModal: Stopping polling");
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

function handleDownload() {
  if (downloadFileLocation.value) {
    FileDownload.downloadFileByURI(`${globalThis.Bootstrap.Config.contentBlobBaseUri}/${downloadFileLocation.value}`);
    emit("update:isVisible", false);
  }
}

// Watch for modal visibility changes
watch(
  () => props.isVisible,
  newVal => {
    if (newVal) {
      isReady.value = false;
      downloadFileLocation.value = null;
      startPolling();
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  stopPolling();
});
</script>
